import { useEffect, useState } from "react";

const MOBILE_REGEX =
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;

/**
 * SEO / social crawlers — always treated as desktop so they mount the
 * desktop React tree (Helmet meta, HeaderPage SEO nav, detail *Seo).
 * Must run before MOBILE_REGEX: smartphone Googlebot contains "Mobile".
 */
const BOT_REGEX =
  /googlebot|google-inspectiontool|googleother|bingbot|bingpreview|slurp|duckduckbot|baiduspider|yandexbot|applebot|facebookexternalhit|twitterbot|linkedinbot|semrushbot|ahrefsbot|dotbot|bytespider|petalbot|\bbot\b|crawler|spider/i;

// Industry-standard mobile breakpoint (Bootstrap/Tailwind md)
const MOBILE_MAX_WIDTH = 767.98;
// Hysteresis thresholds to prevent flicker around breakpoint
const MOBILE_TO_DESKTOP_MIN = 800; // Stay mobile until width >= 800
const DESKTOP_TO_MOBILE_MAX = 736; // Stay desktop until width < 736

const STORAGE_KEY = "gst-ui-mode";

/**
 * react-snap injects this before any app JS. HeadlessChrome is not Googlebot,
 * so bot UA alone does not keep prerender on the desktop tree.
 */
function isSnapRender(): boolean {
  return typeof window !== "undefined" && Boolean((window as any).__IS_SNAP_RENDER);
}

/**
 * Check if the user agent is a search engine or social media crawler.
 * SEO bots always receive desktop UI for consistent indexing.
 */
function isSeoBot(userAgent: string): boolean {
  return BOT_REGEX.test(userAgent);
}

/**
 * Detect if device should use mobile UI based on user agent.
 */
function hasMobileUA(): boolean {
  if (typeof navigator === "undefined") return false;
  return MOBILE_REGEX.test(navigator.userAgent);
}

/**
 * Check if viewport width indicates mobile device.
 */
function hasNarrowViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/**
 * Determine initial mobile mode based on UA, viewport width, and SEO bot detection.
 * SEO bots always get desktop. Others get mobile if UA or viewport indicates mobile.
 */
function detectInitialMode(): boolean {
  const logdetails: string[] = ["detectInitialMode"];
  const _logger = console.log;

  if (typeof navigator === "undefined") {
    logdetails.push("ssr=desktop");
    _logger("[useIsMobile]", logdetails.join(" | "));
    return false;
  }

  // Prerender and crawlers always get desktop (Helmet, HeaderPage, detail *Seo)
  if (isSnapRender()) {
    logdetails.push("snap=desktop");
    _logger("[useIsMobile]", logdetails.join(" | "));
    return false;
  }

  const ua = navigator.userAgent;
  if (isSeoBot(ua)) {
    logdetails.push("bot=desktop");
    _logger("[useIsMobile]", logdetails.join(" | "));
    return false;
  }

  const mobile = hasMobileUA() || hasNarrowViewport();
  logdetails.push(`uaMobile=${hasMobileUA()}`, `narrow=${hasNarrowViewport()}`, `mode=${mobile ? "mobile" : "desktop"}`);
  _logger("[useIsMobile]", logdetails.join(" | "));
  return mobile;
}

/**
 * Read UI mode from sessionStorage. Returns null if not set.
 */
function getStoredMode(): boolean | null {
  if (isSnapRender()) return null;
  if (typeof sessionStorage === "undefined") return null;
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored === "mobile") return true;
  if (stored === "desktop") return false;
  return null;
}

/**
 * Write UI mode to sessionStorage.
 */
function setStoredMode(isMobile: boolean): void {
  if (isSnapRender()) return;
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(STORAGE_KEY, isMobile ? "mobile" : "desktop");
}

/**
 * Handle optional URL override (?mode=mobile or ?mode=desktop).
 * If present, store in sessionStorage and remove from URL.
 */
function handleUrlOverride(): boolean | null {
  if (typeof window === "undefined") return null;
  
  const params = new URLSearchParams(window.location.search);
  const modeParam = params.get("mode");
  
  if (modeParam === "mobile" || modeParam === "desktop") {
    const isMobile = modeParam === "mobile";
    setStoredMode(isMobile);
    
    // Remove mode parameter from URL without reload
    params.delete("mode");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    
    return isMobile;
  }
  
  return null;
}

/**
 * Hook that detects mobile vs desktop UI mode with sessionStorage latching
 * and hysteresis on resize to prevent UI flicker.
 * 
 * - react-snap (__IS_SNAP_RENDER) and SEO bots always get desktop
 * - First visit: mobile if UA is mobile OR viewport width <= 767.98px
 * - Navigation within session: reads latched mode from sessionStorage
 * - Resize: applies hysteresis (mobile→desktop at >=800px, desktop→mobile at <736px)
 * - Optional URL override: ?mode=mobile or ?mode=desktop (removed after storing)
 */
export function useIsMobile(): boolean {
  // Initialize from URL override, sessionStorage, or detection
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // Snap + SEO bots always desktop so prerendered HTML is HomePage / detail *Seo
    if (isSnapRender()) {
      return false;
    }
    if (typeof navigator !== "undefined" && isSeoBot(navigator.userAgent)) {
      return false;
    }
    
    // Check for URL override first
    const urlOverride = handleUrlOverride();
    if (urlOverride !== null) return urlOverride;
    
    // Check sessionStorage for latched mode
    const storedMode = getStoredMode();
    if (storedMode !== null) return storedMode;
    
    // First visit: detect from UA and viewport
    const initialMode = detectInitialMode();
    setStoredMode(initialMode);
    return initialMode;
  });

  useEffect(() => {
    // Snap and SEO bots stay on the desktop tree
    if (isSnapRender()) {
      return;
    }
    if (typeof navigator !== "undefined" && isSeoBot(navigator.userAgent)) {
      return;
    }

    /**
     * Handle viewport resize with hysteresis to prevent flicker.
     * Only switch modes when crossing the hysteresis band.
     */
    function handleResize() {
      if (typeof window === "undefined") return;
      
      const width = window.innerWidth;
      let shouldSwitch = false;
      let newMode = isMobile;

      if (isMobile && width >= MOBILE_TO_DESKTOP_MIN) {
        // Switch from mobile to desktop
        shouldSwitch = true;
        newMode = false;
      } else if (!isMobile && width < DESKTOP_TO_MOBILE_MAX) {
        // Switch from desktop to mobile
        shouldSwitch = true;
        newMode = true;
      }

      if (shouldSwitch) {
        setIsMobile(newMode);
        setStoredMode(newMode);
      }
    }

    // Listen for viewport resize
    window.addEventListener("resize", handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile]);

  return isMobile;
}
